import { useEffect, useState } from "react";
import { X, Waves, MapPin, ShieldAlert, CalendarClock, Building2 } from "lucide-react";
import { INFRA_HEX, infraLabel } from "@/lib/maritime";
import api from "@/lib/api";

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
    <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{label}</span>
    <span className="text-sm text-slate-200 font-medium text-right">{value ?? "—"}</span>
  </div>
);

export const InfraPanel = ({ gfwId, onClose }) => {
  const [d, setD] = useState(null);

  useEffect(() => {
    let live = true;
    setD(null);
    api.infrastructureDetail(gfwId).then((x) => live && setD(x)).catch(() => {});
    return () => {
      live = false;
    };
  }, [gfwId]);

  const hex = d ? INFRA_HEX[d.label] || INFRA_HEX.unknown : INFRA_HEX.oil;

  return (
    <aside
      data-testid="infra-detail-panel"
      className="absolute top-16 right-4 bottom-16 z-20 w-80 md:w-96 flex flex-col rounded-xl bg-slate-900/95 backdrop-blur-xl border border-slate-800/80 shadow-2xl overflow-hidden st-fade"
    >
      <div className="flex items-start justify-between p-4 border-b border-slate-800/80">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="h-3 w-3 rotate-45 rounded-[2px] shrink-0" style={{ backgroundColor: hex }} />
            <span
              className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded"
              style={{ color: hex, background: `${hex}22` }}
            >
              {d ? infraLabel(d.label) : "…"}
            </span>
          </div>
          <h2 className="font-display text-lg font-bold text-white truncate">
            {d?.name || d?.field || "Offshore Structure"}
          </h2>
          <p className="font-mono text-[11px] text-slate-500 mt-0.5">GFW ID · {d?.gfw_id}</p>
        </div>
        <button
          data-testid="infra-panel-close"
          onClick={onClose}
          className="text-slate-500 hover:text-slate-200 transition-colors shrink-0"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto st-scroll p-4 space-y-4">
        <div className="rounded-lg bg-slate-800/40 border border-slate-700/50 px-3 py-1">
          <Row label="Detection label" value={d ? infraLabel(d.label) : "—"} />
          <Row label="Confidence" value={d?.confidence} />
          <Row label="First detected" value={d?.first_seen} />
          <Row label="Last detected" value={d?.last_seen} />
          <Row label="Position" value={d ? `${d.lat?.toFixed(4)}, ${d.lon?.toFixed(4)}` : "—"} />
          <Row label="Operator" value={d?.operator || "Unknown"} />
        </div>

        <div className="rounded-lg bg-slate-800/40 border border-slate-700/50 p-3">
          <div className="flex items-center gap-2 text-slate-300 mb-2">
            <Waves className="h-4 w-4 text-[#FF2A6D]" />
            <span className="text-xs font-semibold">Slick history</span>
          </div>
          <p className="font-mono text-2xl font-bold text-white">
            {d?.slick_history_count ?? 0}
            <span className="text-xs text-slate-500 ml-2 font-medium">potential slicks nearby</span>
          </p>
          <p className="text-[11px] text-slate-500 mt-1">
            Populated in Phase 2 from SkyTruth Cerulean detections within the buffer over the retained period —
            helps distinguish a one-off event from a chronic leak.
          </p>
        </div>

        <div className="rounded-lg bg-slate-800/30 border border-dashed border-slate-700/50 p-3 flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-500/70 mt-0.5 shrink-0" />
          <p className="text-[11px] leading-relaxed text-slate-500">
            SAR/optical detection, not a registry. Some entries may be buoys, wrecks or fish farms.
            No operator or field names are asserted by the source.
          </p>
        </div>
      </div>
    </aside>
  );
};

export default InfraPanel;
